// A shared world in a buddy session (docs/COLLABORATION_DESIGN.md, "World
// transactions"): the page passes the World Editor's session messages to
// the room (session.js, web/server/lib/rooms.mjs) and the room's to the
// editor, over the page channel (PageMessage and SendPageMessage). The editor
// applies its own edits at once and keeps their inverse operations; the room
// puts everyone's edits in one order.
//
// However the editor came to have a world open, as the IDE started it or
// with its own File > Open, New or Save As, it says which (opened), and the
// relay joins that world when the session has its file, or leaves the one it
// was in. A watcher's editor joins read-only.
//
// When the World Editor saves the shared world, the room hears of it first
// (saving), so that it can tell a save from a change made to the file outside
// the editors, after which it loads the world again from the file.
//
// The editor's messages, protocol "blitz3d.editor-session", version 1:
//
//   opened     { file, document }: the world the editor has open now, by its
//              path in the editor ("" for a new world not yet saved) and its
//              document's number; said once the page asks (report), and
//              whenever it changes
//   hello      { world }: the editor joined the world; it answers with start
//   left       { world }: the editor left the world's session
//   edit       { id, base, label, touches, changes, since, operations,
//              terrain }: an edit made in the editor, after the edits up to
//              base; changes are the ids of touches it changed rather than
//              only read; an Undo or Redo gives since, the step's number, to
//              be checked against everyone else's edits after it; a terrain
//              stroke's (terrain: { patches, creates }) may have no
//              operations, and its heights or pixels come in the file sent
//              with it, which goes to the room as a file of its own (data)
//   broken     { seq }: the room's edit seq could not be applied there, and
//              was passed over
//   checkpoint { seq, text, saved, terrain }: the world at the room's edit
//              seq, after a save (saved) or when the room asked; terrain, {
//              docs }, lists the heights and blend maps not saved, whose data
//              come in the file sent with it
//   hash       { seq, hash }
//   presence   { selection: [ids], camera, drag, assistants }
//
// and the page's to the editor, each about the world the relay joined:
//
//   report     {}: say which world is open, now and whenever it changes
//   join       { world, document, watching }: the world the editor said it
//              has open as document is the session's; it answers with hello
//   leave      {}: the session is over here; the editor's world is its own
//   start      { world, seq, base: { seq, text, hash, terrain }, log: [edits],
//              saved }: the world as the room has it; the editor makes its
//              document base's text, then applies the log's edits; saved is
//              the room's edit at which the world was last saved
//   remote     { seq, id, by, name, label, touches, operations, terrain,
//              broken }:
//              someone else's edit, to apply between frames, or after a drag
//              ends; one that touches what an edit of the editor's not yet
//              answered touched means the room will refuse that edit; one
//              marked broken is passed over
//   ack        { id, seq }: an edit of the editor's was accepted
//   refused    { id, seq, winner: { name, label }, resync, reason }: an edit of
//              the editor's was not; it takes it back with its inverse
//              operations; reason "full" when the room's log of the world
//              waits for a checkpoint
//   resync     { changedBy, kept }: the editor's world differs from the
//              host's, or someone changed its file outside the World Editor
//              (changedBy names them); it starts again from start
//   saved      { seq, name }: someone else saved the world as it was at seq
//   checkpoint {}: the room asks the editor for a checkpoint
//   people     { people: [{ id, name, colour, selection, camera, drag, assistants }] }:
//              where the others are in the world, at most ten times a second
//
// A terrain's data the room has as a file reaches the editor before the
// message naming it: the page puts it in the running editor's files
// (putFile), and terrain.file says where. The room's messages therefore go to
// the editor one after another, in order, each once its data is in place,
// and the editor's go to the room in the order it sent them.

export const PROTOCOL = "blitz3d.editor-session";

// Checkpoints larger than this go to the room as a file of their own, as
// large worlds do.
export const CHECKPOINT_TEXT_BYTES = 1 << 20;

// Others' presence is theirs to set, so only what it should hold goes to the
// editor, and only so much of it (editor_session.bb checks it again): ids,
// a view of three-number lists, and dragged objects with three-number lists.
const REACH = 1e6;
const vector = (value) => Array.isArray(value) && value.length === 3 &&
  value.every((number) => typeof number === "number" && Number.isFinite(number) && Math.abs(number) <= REACH) ? value : null;
const idList = (value, limit) => Array.isArray(value) ?
  value.filter((id) => typeof id === "string" && id.length <= 200).slice(0, limit) : [];
export function presenceView(camera) {
  if (!camera || typeof camera !== "object") return null;
  const target = vector(camera.target), angles = vector(camera.angles);
  return target && angles ? { target, angles, ortho: camera.ortho === true } : null;
}
export function presenceDrag(drag) {
  if (!Array.isArray(drag)) return null;
  const objects = drag.slice(0, 64).filter((entry) => entry && typeof entry === "object" &&
    typeof entry.id === "string" && entry.id.length <= 200 && vector(entry.position) && vector(entry.angles) &&
    vector(entry.scale)).map(({ id, position, angles, scale }) => ({ id, position, angles, scale }));
  return objects.length ? objects : null;
}
// A person's Blip in the editor's views (editor_assistants.bb): where he is,
// which way he faces, what he is doing, his size and what he works near.
const ASSISTANT_STATES = ["working", "thinking", "finishing", "resting", "asleep"];
export function presenceAssistants(assistants) {
  if (!Array.isArray(assistants)) return null;
  const kept = assistants.slice(0, 4).filter((entry) => entry && typeof entry === "object" && vector(entry.position) &&
    ASSISTANT_STATES.includes(entry.state)).map((entry) => ({ position: entry.position, state: entry.state,
    yaw: typeof entry.yaw === "number" && Number.isFinite(entry.yaw) ? entry.yaw : 0,
    size: typeof entry.size === "number" && entry.size >= 0.4 && entry.size <= 2 ? entry.size : 1,
    ...(typeof entry.near === "string" && entry.near.length <= 200 ? { near: entry.near } : {}) }));
  return kept.length ? kept : null;
}

// The SHA-256 of a text's UTF-8 bytes, in hex, as the room hashes text.
export async function textHash(text) {
  return bytesHash(new TextEncoder().encode(text));
}

// The SHA-256 of bytes, in hex, as the room names its files.
export async function bytesHash(bytes) {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

// Where the page puts a terrain's data from the room in the editor's files,
// under /app: a file named by its hash.
export const dataPath = (hash) => `.buddies/${hash}.bin`;

// session: a BuddySession. send(message): to the editor. refused({ mine,
// winner }), when given, hears of each edit of the editor's the room refused,
// with the edit that won when it was seen here, each { name, label,
// operations }, for Ask Blip to combine. worldOf(file) gives the project's
// path of a file as the editor names it, or null. settle(), when given, waits
// for the files the editor saved to reach the project and the room.
// putFile(path, bytes), when given, gives the running editor a file under
// /app, for terrain data. Returns { world, report(), receive(message from the
// editor, the file sent with it), saving(path, text), recheck(), close() }.
export function shareWorld(session, send, { refused = null, worldOf = (file) => file || null, settle = null,
  putFile = null } = {}) {
  let world = null;               // the session's world the editor is in, or is joining
  let number = null;              // and the editor's number for its document
  let open = null;                // the world the editor said it has open: { path, document }
  const post = (type, fields = {}) => send({ protocol: PROTOCOL, version: 1, type, ...(world ? { world } : {}), ...fields });
  const mine = new Map();         // the editor's edits not yet answered, by id
  const recent = new Map();       // others' recent edits, by sequence number

  // The room's messages to the editor, and the editor's to the room, each in
  // order: a terrain's data is fetched or sent before its message goes on.
  // A task runs at once while nothing before it waits, so that messages
  // without data go on as they come.
  const queue = () => {
    let waiting = null;
    const settle = (chain) => chain.catch((error) => console.error("Buddy session:", error))
      .then(() => { if (waiting === chain) waiting = null; });
    return (task) => {
      if (!waiting) {
        let result;
        try {
          result = task();
        } catch (error) {
          console.error("Buddy session:", error);
          return Promise.resolve();
        }
        if (!result || typeof result.then !== "function") return Promise.resolve();
        const chain = Promise.resolve(result);
        waiting = chain;
        return settle(chain);
      }
      const chain = waiting.catch(() => {}).then(task);
      waiting = chain;
      return settle(chain);
    };
  };
  const editorTask = queue(), roomTask = queue();
  // A terrain description with its data put in the editor's files (file), or
  // as it was when it has none.
  const terrainWithData = async (terrain, data) => {
    if (!terrain || typeof terrain !== "object") return null;
    if (!data || typeof data.blob !== "string" || !putFile) return terrain;
    const bytes = new Uint8Array(await (await session.download(data.blob)).arrayBuffer());
    const path = dataPath(data.blob);
    putFile(path, bytes);
    return { ...terrain, file: `/app/${path}` };
  };
  // A file the editor sent, as a file of the room's: { blob, size }.
  const sendData = async (file) => {
    const raw = file && (file.data || file.bytes || file);
    if (!raw || (typeof raw.byteLength !== "number")) return null;
    const bytes = raw instanceof Uint8Array ? raw : new Uint8Array(raw.buffer || raw, raw.byteOffset || 0, raw.byteLength);
    const hash = await bytesHash(bytes);
    await session.upload(hash, new Blob([bytes]));
    return { blob: hash, size: bytes.byteLength };
  };

  const listeners = [];
  const on = (type, handler) => {
    const listener = (event) => {
      if (event.detail && event.detail.world === world) {
        const asked = world, detail = event.detail;
        editorTask(() => (world === asked ? handler(detail) : null));
      }
    };
    session.addEventListener(type, listener);
    listeners.push([type, listener]);
  };
  on("world-edit", (edit) => {
    if (session.you && edit.by === session.you.id && mine.delete(edit.id)) {
      post("ack", { id: edit.id, seq: edit.seq });
      return null;
    }
    const remote = (terrain) => {
      post("remote", { seq: edit.seq, id: edit.id, by: edit.by, name: edit.name, label: edit.label,
        touches: edit.touches || [], operations: edit.operations, ...(terrain ? { terrain } : {}),
        ...(edit.broken ? { broken: true } : {}) });
      recent.set(edit.seq, { name: edit.name, label: edit.label, operations: edit.operations });
      if (recent.size > 50) recent.delete(recent.keys().next().value);
    };
    if (edit.broken || !edit.terrain) return remote(null);
    if (!edit.data || !putFile) return remote(edit.terrain);
    // Its heights or pixels reach the editor's files first.
    const asked = world;
    return terrainWithData(edit.terrain, edit.data).then((terrain) => {
      if (world === asked) remote(terrain);
    });
  });
  on("world-refused", (refusal) => {
    const edit = mine.get(refusal.id);
    mine.delete(refusal.id);
    if (edit && refused && refusal.winner && !refusal.resync) {
      const won = recent.get(refusal.winner.seq);
      refused({ mine: edit, winner: { name: refusal.winner.name, label: refusal.winner.label,
        operations: won ? won.operations : null } });
    }
    post("refused", { id: refusal.id, seq: refusal.seq, winner: refusal.winner || null, resync: !!refusal.resync,
      ...(refusal.reason ? { reason: refusal.reason } : {}) });
  });
  // Loaded again: out of step, or someone changed the world's file outside
  // the World Editor (changedBy), when the changes not yet saved were made
  // again on top (kept).
  on("world-resync", (resync) => post("resync", typeof resync.changedBy === "string" ?
    { changedBy: resync.changedBy, kept: !!resync.kept } : {}));
  on("world-saved", (saved) => {
    if (!session.you || saved.by !== session.you.id) post("saved", { seq: saved.seq, name: saved.name || "" });
  });
  on("world-checkpoint-wanted", () => post("checkpoint"));

  // Where the others are in this world, ten times a second at most.
  let peopleTimer = 0;
  const people = () => {
    if (peopleTimer) return;
    peopleTimer = setTimeout(() => {
      peopleTimer = 0;
      if (!world) return;
      const list = [];
      for (const person of session.people) {
        if (session.you && person.id === session.you.id) continue;
        const state = session.presenceOf(person);
        const place = state && state.world;
        if (!place || place.path !== world) continue;
        // While Blip answers someone, it is his figure that says so
        // ("Blip (asked by Bea)"), and their marker keeps their name.
        list.push({ id: person.id, name: person.name, colour: person.colour, selection: idList(place.selection, 500),
          camera: presenceView(place.camera), drag: presenceDrag(place.drag), assistants: presenceAssistants(place.assistants) });
      }
      post("people", { people: list });
    }, 100);
  };
  const presence = () => people();
  session.addEventListener("presence", presence);
  session.addEventListener("people", presence);
  // After the page reconnects, the editor's edits sent while it was away may
  // be lost, and the others' made then were not heard: the editor loads the
  // world again as the room has it.
  const welcome = () => {
    if (!world) return;
    mine.clear();
    post("resync");
  };
  session.addEventListener("welcome", welcome);

  // The relay leaves the world it was in; the editor has left its session
  // already, or is told to.
  const leaveWorld = () => {
    if (!world) return;
    if (!session.stopped) session.sendWorldClose(world);
    world = null;
    number = null;
    mine.clear();
    recent.clear();
    session.setPresence({ world: null });
  };
  // Joins the world the editor has open, when the session has it, or leaves
  // the one it was in.
  let deciding = Promise.resolve();
  const decide = () => {
    deciding = deciding.then(async () => {
      if (settle) await settle();
      const path = open && open.path;
      if (world && path === world && open.document === number) return;
      leaveWorld();
      if (!path || !/\.world$/i.test(path) || session.stopped || !session.hasFile(path)) return;
      world = path;
      number = open.document;
      post("join", { document: number, watching: !session.canEdit });
    }).catch((error) => console.error("Buddy session:", error));
    return deciding;
  };

  return {
    get world() { return world; },
    get session() { return session; },
    // Asks the editor which world it has open.
    report() {
      send({ protocol: PROTOCOL, version: 1, type: "report" });
    },
    // A message from the editor, and the file sent with it ({ data }), if any.
    async receive(message, file = null) {
      if (!message || message.protocol !== PROTOCOL) return false;
      switch (message.type) {
        case "opened": {
          const path = typeof message.file === "string" && message.file ? worldOf(message.file) : null;
          open = { path: path || null, document: Number.isInteger(message.document) ? message.document : null };
          await decide();
          break;
        }
        case "hello": {
          if (!world || message.world !== world) break;
          const asked = world;
          await editorTask(async () => {
            if (world !== asked) return;
            const state = await session.openWorld(asked);
            if (world !== asked) return;
            let base = state.base;
            // A large world comes as a file of its own.
            if (base && typeof base.blob === "string") base = { ...base, text: await session.worldText(base.blob) };
            // Heights and blend maps not saved, and the log's terrain strokes,
            // are put in the editor's files first.
            if (base && base.terrain) {
              base = { seq: base.seq, hash: base.hash, text: base.text, terrain: await terrainWithData(base.terrain, base.terrainData) };
            }
            const log = [];
            for (const entry of state.log || []) {
              const terrain = !entry.broken && entry.terrain ? await terrainWithData(entry.terrain, entry.data) : null;
              const { data, ...rest } = entry;
              log.push(terrain ? { ...rest, terrain } : rest);
            }
            if (world !== asked) return;
            post("start", { seq: state.seq, base, log, ...(Number.isInteger(state.saved) ? { saved: state.saved } : {}) });
            people();
          });
          break;
        }
        case "left":
          if (world && message.world === world) leaveWorld();
          break;
        case "edit": {
          if (!world || message.world !== world || !session.canEdit) return true;
          const asked = world;
          await roomTask(async () => {
            // A terrain stroke's heights or pixels go first, as a file of the room's.
            const data = message.terrain && file ? await sendData(file) : null;
            if (world !== asked) return;
            mine.set(message.id, { name: session.you ? session.you.name : "", label: message.label || "",
              operations: message.operations });
            session.sendWorldEdit({ world, base: message.base, id: message.id, label: message.label || "",
              touches: message.touches, ...(Array.isArray(message.changes) ? { changes: message.changes } : {}),
              ...(Number.isInteger(message.since) ? { since: message.since } : {}), operations: message.operations,
              ...(message.terrain ? { terrain: message.terrain } : {}), ...(data ? { data } : {}) });
          });
          break;
        }
        case "broken":
          if (world && message.world === world && Number.isInteger(message.seq)) {
            const asked = world;
            await roomTask(() => (world === asked ? session.sendWorldBroken(world, message.seq) : null));
          }
          break;
        case "checkpoint": {
          if (!world || message.world !== world || !session.canEdit || typeof message.text !== "string") break;
          const asked = world;
          await roomTask(async () => {
            const hash = await textHash(message.text);
            const checkpoint = { seq: message.seq, hash, ...(message.saved ? { saved: true } : {}) };
            if (new TextEncoder().encode(message.text).length > CHECKPOINT_TEXT_BYTES) {
              await session.upload(hash, new Blob([message.text]));
              checkpoint.blob = hash;
            } else {
              checkpoint.text = message.text;
            }
            // Heights and blend maps not saved, their data as a file of the room's.
            if (message.terrain && typeof message.terrain === "object") {
              checkpoint.terrain = message.terrain;
              const data = file ? await sendData(file) : null;
              if (data) checkpoint.terrainData = data;
            }
            if (world === asked) session.sendWorldCheckpoint(world, checkpoint);
          });
          break;
        }
        case "hash":
          if (world && message.world === world) {
            const asked = world;
            await roomTask(() => (world === asked ? session.sendWorldHash(world, message.seq, message.hash) : null));
          }
          break;
        case "presence":
          if (world && message.world === world) {
            session.setPresence({ world: { path: world, selection: message.selection || [], camera: message.camera || null,
              drag: message.drag || null, assistants: message.assistants || null } });
          }
          break;
      }
      return true;
    },
    // The editor is saving the shared world's file, as text: the room hears of
    // it first, so that it can tell the save from a change made outside the
    // World Editor, which it loads the world again from.
    async saving(path, text) {
      if (!world || path !== world || !session.canEdit) return;
      session.sendWorldSaving(world, await textHash(text.replace(/\r\n?/g, "\n")));
    },
    // A file the editor saved reached the project: a world the editor has open
    // and the session did not have yet may be the session's now.
    recheck() {
      if (open && open.path && open.path !== world) decide();
    },
    // The session is over here, or the editor closed.
    close() {
      clearTimeout(peopleTimer);
      for (const [type, listener] of listeners) session.removeEventListener(type, listener);
      session.removeEventListener("presence", presence);
      session.removeEventListener("people", presence);
      session.removeEventListener("welcome", welcome);
      if (world) post("leave");
      leaveWorld();
      open = null;
    },
  };
}
